import { Modal, ModalBody } from 'reactstrap';
import useTournamentDetails from '@/hooks/useTournamentDetails';
import { useParams } from 'react-router-dom';
import DataTable from '@/common/tableData/DataTable';
import { format } from 'date-fns';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  restartTableRest,
  startTableHandRest,
  stopTableRest,
  updateTableStatusRest,
} from '@/services/table/table-service';
import {
  deleteTableServerRest,
  setTournamentServerTableActiveRest,
  setTournamentTableActiveRest,
} from '@/services/tournaments/tournaments-service';
import TableActionBar from '@/components/PokerTable/TableActionBar';
import './TournamentTableDetailsModal.css';
import { TableStatus } from '@/common/types/phenomTypes';

const TournamentTableDetailsModal = ({ onClose, table }) => {
  const { tournamentId } = useParams();
  const { tournamentEvents, tournament } = useTournamentDetails(tournamentId ?? '');

  const [showConfirmModal, setShowConfirmModal] = useState<{
    type: 'restart-table' | 'start-hand' | 'kill-server';
    tableId: string;
  } | null>(null);

  const [isRefreshingCache] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [activeState, setActiveState] = useState<boolean>(Boolean(table?.table?.isActive ?? true));
  const [statusState, setStatusState] = useState<TableStatus | undefined>(table?.table?.status);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [showEvents, setShowEvents] = useState(false);

  const tableEvents = useMemo(() => {
    const list = tournamentEvents?.filter((event) => event.tableId === table?.table.id) ?? [];
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tournamentEvents, table]);

  // Transform players data for PokerTableView
  const transformedPlayers = useMemo(() => {
    if (!table?.players || !Array.isArray(table.players)) {
      return [];
    }

    return table.players.map((player) => ({
      userId: player.userId,
      alias: player.alias,
      seat: player.seat + 1, // Convert from 0-indexed to 1-indexed
      stack: player.stack,
      isActing: false,
      isDealer: false,
    }));
  }, [table?.players]);

  useEffect(() => {
    setStatusState(table?.table?.status);
  }, [table?.table?.status]);

  const statusOptions: TableStatus[] = [TableStatus.RUNNING, TableStatus.STOPPED];
  const statusTextClass =
    statusState === TableStatus.RUNNING
      ? 'status-running'
      : statusState === TableStatus.STOPPED
        ? 'status-stopped'
        : 'status-unknown';

  const handleConfirmRestartTable = async () => {
    try {
      await restartTableRest(table?.table.id, false);
      toast.success('Table restarted successfully');
      setShowConfirmModal(null);
      onClose();
    } catch (error) {
      toast.error('Error restarting table');
    }
  };

  const handleConfirmStartHand = async () => {
    try {
      if (!showConfirmModal?.tableId || !tournamentId || !tournament?.url) {
        toast.error('Error starting hand');
        return;
      }
      await startTableHandRest(tournament?.url, showConfirmModal?.tableId, tournamentId);
      toast.success('Table hand queued for start');
      setShowConfirmModal(null);
      onClose();
    } catch (error) {
      toast.error('Error starting hand');
    }
  };

  const handleConfirmKillServer = async () => {
    try {
      if (!showConfirmModal?.tableId) {
        toast.error('Error killing server');
        return;
      }
      await deleteTableServerRest(showConfirmModal.tableId);
      await stopTableRest(showConfirmModal.tableId, false);
      toast.success('Table server killed successfully');
      setShowConfirmModal(null);
      onClose();
    } catch (error) {
      toast.error('Error killing server');
    }
  };

  const handleToggleActive = async () => {
    if (!tournamentId || !table?.table?.id) return;
    const nextActive = !activeState;
    setIsBusy(true);
    try {
      // Fire both in parallel: persist to lobby DB, best-effort cache update on tournament-server
      const tasks: Promise<unknown>[] = [setTournamentTableActiveRest(tournamentId, table.table.id, nextActive)];
      if (tournament?.url) {
        tasks.push(setTournamentServerTableActiveRest(tournament.url, table.table.id, nextActive));
      }
      await Promise.allSettled(tasks);
      setActiveState(nextActive);
      toast.success(`Table ${nextActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update table active state');
    } finally {
      setIsBusy(false);
    }
  };

  // Attach disabled/removed for now
  const handleStatusChange = async (nextStatus: TableStatus) => {
    if (!table?.table?.id || nextStatus === statusState) {
      return;
    }

    setIsStatusUpdating(true);
    try {
      await updateTableStatusRest(table.table.id, nextStatus);
      setStatusState(nextStatus);
      toast.success(`Table status updated to ${nextStatus}`);
    } catch (error) {
      console.error('Failed to update table status', error);
      toast.error('Failed to update table status');
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleConfirmAction = () => {
    switch (showConfirmModal?.type) {
      case 'restart-table':
        handleConfirmRestartTable();
        break;
      case 'start-hand':
        handleConfirmStartHand();
        break;
      case 'kill-server':
        handleConfirmKillServer();
        break;
    }
  };

  const handleViewHandHistory = () => {
    window.open(
      `${process.env.REACT_APP_PHENOM_APP}/hand-history/tournament/${tournamentId}/table/${table?.table.id}?showHoleCards=true`,
      '_blank',
    );
  };

  const handleCopyTableId = () => {
    if (table?.table?.id) {
      navigator.clipboard.writeText(table.table.id);
      toast.success('Table ID copied to clipboard');
    }
  };

  if (showConfirmModal) {
    const getConfirmModalProps = () => {
      switch (showConfirmModal.type) {
        case 'restart-table':
          return {
            title: 'Restart Table',
            message: 'Are you sure you want to restart this table?',
          };
        case 'start-hand':
          return {
            title: 'Start Hand',
            message: 'Are you sure you want to start a hand on this table?',
          };
        case 'kill-server':
          return {
            title: 'Kill Server',
            message:
              '⚠️ This will permanently delete the game server from Agones/K8s. Are you sure you want to kill this table server?',
          };
        default:
          return {
            title: 'Confirm',
            message: 'Are you sure?',
          };
      }
    };

    const { title, message } = getConfirmModalProps();

    return (
      <ConfirmModal
        title={title}
        message={message}
        onConfirm={handleConfirmAction}
        onClose={() => setShowConfirmModal(null)}
        confirmButtonText={showConfirmModal.type === 'kill-server' ? 'Yes, Kill Server' : undefined}
        cancelButtonText={showConfirmModal.type === 'kill-server' ? 'Cancel' : undefined}
      />
    );
  }

  const tableName = (() => {
    if (table?.table?.tableNumber) {
      return `Table #${table.table.tableNumber}`;
    }
    const match = table?.table?.name?.match(/Table #\d+/);
    return match ? match[0] : table?.table?.name;
  })();

  const playerCount = table?.players?.length || 0;
  const maxPlayers = table?.table?.maxPlayers || 9;

  return (
    <Modal isOpen toggle={onClose} className="modal-xl tournament-table-modal" centered backdrop="static">
      <div className="modal-content border-0 shadow-lg">
        {/* Header */}
        <div className="modal-header-custom">
          <div className="header-content">
            <div className="header-info">
              <div className="header-text">
                <h4 className="table-title">{tableName}</h4>
                <div className="table-badges">
                  <span className="info-badge">
                    <i className="ri-user-line"></i>
                    {playerCount}/{maxPlayers} Players
                  </span>
                  <span className="info-badge">
                    <i className="ri-time-line"></i>
                    Created {format(new Date(table?.table.createdAt), 'HH:mm')}
                  </span>
                  <span className={`info-badge ${statusTextClass}`}>
                    <i className="ri-speed-up-line"></i>
                    Status: {statusState ?? 'UNKNOWN'}
                  </span>
                  <span className={`state-badge ${activeState ? 'active' : 'inactive'}`}>
                    <i className={activeState ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}></i>
                    {activeState ? 'Active' : 'Inactive'}
                  </span>
                  <span className="info-badge table-id-badge">
                    <i className="ri-hashtag"></i>
                    {table?.table?.id}
                    <button onClick={handleCopyTableId} className="copy-id-button" title="Copy table ID">
                      <i className="ri-file-copy-line"></i>
                    </button>
                  </span>
                  {table?.table?.url && (
                    <span className="info-badge table-url-badge">
                      {table.table.url}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(table.table.url);
                          toast.success('Table URL copied to clipboard');
                        }}
                        className="copy-id-button"
                        title="Copy table URL"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button type="button" className="close-button" onClick={onClose}>
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <ModalBody className="p-0">
          {/* Action Bar */}
          <TableActionBar
            onStartHand={() => setShowConfirmModal({ type: 'start-hand', tableId: table?.table.id })}
            onRestartService={() => setShowConfirmModal({ type: 'restart-table', tableId: table?.table.id })}
            onKillServer={() => setShowConfirmModal({ type: 'kill-server', tableId: table?.table.id })}
            onViewHandHistory={handleViewHandHistory}
            tournamentUrl={tournament?.url || undefined}
            tableId={table?.table?.id || undefined}
            isRefreshingCache={isRefreshingCache}
            disabled={isBusy}
            isActive={activeState}
            onToggleActive={handleToggleActive}
            currentStatus={statusState}
            statusOptions={statusOptions}
            onStatusChange={handleStatusChange}
            isStatusUpdating={isStatusUpdating}
          />

          {/* Table Players */}
          <div className="table-section">
            <div className="players-table-container">
              <h6 className="players-title">
                <i className="ri-group-line"></i>
                Players ({playerCount}/{maxPlayers})
              </h6>

              {transformedPlayers.length > 0 ? (
                <div className="players-grid">
                  {transformedPlayers.map((player) => (
                    <div key={player.userId} className="player-card">
                      <div className="player-info">
                        <div className="player-seat">Seat {player.seat}</div>
                        <div className="player-name">{player.alias}</div>
                      </div>
                      <div className="player-stack">{player.stack.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-players">
                  <i className="ri-user-line"></i>
                  <span>No players at this table</span>
                </div>
              )}
            </div>
          </div>

          {/* Events Section */}
          <div className="events-section">
            <button
              className="events-header-toggle"
              type="button"
              onClick={() => setShowEvents((prev) => !prev)}
              aria-expanded={showEvents}
            >
              <div className="events-title-group">
                <div className={`chevron ${showEvents ? 'open' : ''}`} aria-hidden="true">
                  <i className="ri-arrow-down-s-line" />
                </div>
                <h6 className="events-title">
                  <i className="ri-history-line"></i>
                  Table Events
                </h6>
              </div>
              <span className="events-count">
                {tableEvents.length} {tableEvents.length === 1 ? 'event' : 'events'}
              </span>
            </button>

            {showEvents && (
              <div className="events-table-container">
                <DataTable
                  columns={[
                    {
                      header: '#',
                      accessorKey: 'index',
                      size: 20,
                      cell: ({ row }) => <div className="event-number">{row.index + 1}</div>,
                    },
                    {
                      header: 'Message',
                      accessorKey: 'message',
                      cell: ({ row }) => <div className="message-cell">{row?.original?.message || '-'}</div>,
                    },
                    {
                      header: 'Event Type',
                      accessorKey: 'eventType',
                      size: 100,
                      cell: ({ row }) => {
                        const formattedEventType = row?.original?.eventType.replace(/_/g, ' ');

                        const getBadgeClass = (event: string) => {
                          const upper = event.toUpperCase();
                          if (upper.includes('START') || upper.includes('INIT')) return 'event-badge-success';
                          if (upper.includes('ELIMINATED') || upper.includes('END') || upper.includes('CANCEL'))
                            return 'event-badge-danger';
                          if (upper.includes('BREAK') || upper.includes('PAUSE') || upper.includes('LEVEL'))
                            return 'event-badge-warning';
                          return 'event-badge-info';
                        };

                        return (
                          <span className={`event-badge ${getBadgeClass(formattedEventType)}`}>
                            {formattedEventType ?? '-'}
                          </span>
                        );
                      },
                    },
                    {
                      header: 'Timestamp',
                      accessorKey: 'createdAt',
                      size: 80,
                      cell: ({ row }) => (
                        <div className="timestamp-cell">
                          <div className="time">{format(new Date(row.original.createdAt), 'HH:mm:ss')}</div>
                          <div className="date">{format(new Date(row.original.createdAt), 'MMM dd')}</div>
                        </div>
                      ),
                    },
                  ]}
                  data={tableEvents}
                  enableSorting={false}
                  pageSize={8}
                />
              </div>
            )}
          </div>
        </ModalBody>
      </div>
    </Modal>
  );
};

export default TournamentTableDetailsModal;
